import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BrandMark } from "../components/AppShell";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    const u = res.user;
    if (u.role === "admin") nav("/admin", { replace: true });
    else if (u.role === "employee" && u.status === "onboarding") nav("/employee/onboarding", { replace: true });
    else nav("/employee", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden border-r border-white/5">
        <img
          src="https://images.unsplash.com/photo-1620584898989-d39f7f9ed1b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxjYXIlMjBkZXRhaWxpbmd8ZW58MHx8fGJsYWNrfDE3NzY2MjcyMzd8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <BrandMark size={44} />
          <div>
            <div className="text-overline mb-4">Operations Portal</div>
            <h2 className="font-heading text-5xl font-bold leading-tight tracking-tight">
              Precision-grade<br /><span className="text-[#FF3B30]">detailing operations.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
              One secure portal for employee punch-ins, dealership tags, customer inspections, and automated payroll reporting.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute inset-0 grid-texture opacity-30 pointer-events-none" />
        <form onSubmit={submit} className="relative w-full max-w-md space-y-6 brand-surface rounded-2xl p-8" data-testid="login-form">
          <div className="lg:hidden flex justify-center mb-4"><BrandMark size={40} /></div>
          <div>
            <div className="text-overline mb-2">Sign In</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome back</h1>
            <p className="text-zinc-500 text-sm mt-2">Access your Dapper Detailing workspace.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#0A0A0A] border-white/10 h-12 focus:border-[#FF3B30]"
                placeholder="you@dapperdetailing.ca" autoComplete="email"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input
                  id="password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-[#0A0A0A] border-white/10 h-12 pr-12 focus:border-[#FF3B30]"
                  placeholder="••••••••" autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white mt-1" data-testid="toggle-password-visibility">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm text-zinc-400 hover:text-[#FF3B30] transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <Button type="submit" disabled={loading}
            className="w-full h-12 bg-[#FF3B30] hover:bg-[#E02920] text-white font-semibold tracking-wide text-sm rounded-lg active:scale-[0.99] transition-transform"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in…" : "Sign In"}
          </Button>

          <p className="text-center text-xs text-zinc-600">
            New employee? You'll receive an invite email from your admin.
          </p>
        </form>
      </div>
    </div>
  );
}
